<template>
	<section
		class="pass-card"
		:aria-labelledby="`pass-${pass.mergePass}-title`"
		:data-testid="`pass-card-${pass.mergePass}`"
	>
		<header class="pass-header">
			<div
				class="pass-badge"
				aria-hidden="true"
			>
				{{ pass.mergePass }}
			</div>
			<div class="pass-title-block">
				<h3
					:id="`pass-${pass.mergePass}-title`"
					class="pass-title"
				>
					{{ 'Pass' | useLabels }} {{ pass.mergePass }}
				</h3>
			</div>
			<div class="pass-controls">
				<v-switch
					inset
					dense
					hide-details
					class="pass-toggle"
					:label="updateDetailsLabel"
					:input-value="pass.updateDetails"
					:disabled="!userFullPermissions"
					:data-testid="`pass-${pass.mergePass}-update-details`"
					@change="$emit('update:updateDetails', $event)"
				/>
				<v-menu
					v-if="userFullPermissions"
					offset-y
					left
				>
					<template #activator="{ on, attrs }">
						<v-btn
							icon
							small
							v-bind="attrs"
							:aria-label="`Actions for pass ${pass.mergePass}`"
							:data-testid="`pass-${pass.mergePass}-menu`"
							v-on="on"
							@click.stop
						>
							<v-icon>
								mdi-dots-horizontal
							</v-icon>
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item
							:disabled="isFirst"
							:data-testid="`pass-${pass.mergePass}-menu-move-up`"
							@click="$emit('move-up')"
						>
							<v-list-item-title>
								{{ 'Move up' | useLabels }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item
							:disabled="isLast"
							:data-testid="`pass-${pass.mergePass}-menu-move-down`"
							@click="$emit('move-down')"
						>
							<v-list-item-title>
								{{ 'Move down' | useLabels }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item
							:data-testid="`pass-${pass.mergePass}-menu-add-rule`"
							@click="$emit('add-rule')"
						>
							<v-list-item-title>
								{{ 'Add rule' | useLabels }}
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</header>

		<div
			class="rule-header"
			aria-hidden="true"
		>
			<div>
				{{ 'Field' | useLabels }}
			</div>
			<div>
				{{ 'Behavior' | useLabels }}
			</div>
			<div class="rule-header--centre">
				{{ 'Status' | useLabels }}
			</div>
			<div />
		</div>

		<div role="list">
			<RuleRow
				v-for="(rule, idx) in pass.rules"
				:key="rule.id || `new-${idx}`"
				:rule="rule"
				:pass-number="pass.mergePass"
				:index="idx"
				:user-full-permissions="userFullPermissions"
				@edit="$emit('edit-rule', idx)"
				@duplicate="$emit('duplicate-rule', idx)"
				@toggle-active="$emit('toggle-rule-active', idx)"
			/>
		</div>

		<button
			v-if="userFullPermissions"
			type="button"
			class="add-rule"
			:data-testid="`pass-${pass.mergePass}-add-rule`"
			@click="$emit('add-rule')"
		>
			+ {{ 'Add rule to this pass' | useLabels }}
		</button>
	</section>
</template>

<script>
import RuleRow from './PassCard.RuleRow.vue'

export default {
	name: 'pass-card',
	components: { RuleRow },
	props: {
		pass: { type: Object, required: true },
		isFirst: { type: Boolean, default: false },
		isLast: { type: Boolean, default: false },
		userFullPermissions: { type: Boolean, default: false }
	},
	// Emits 'update:updateDetails' with the new boolean value — parents may listen via
	// @update:updateDetails or use .sync on :pass.updateDetails.
	computed: {
		updateDetailsLabel () {
			return this.$options.filters.useLabels('Update details on match')
		}
	}
}
</script>

<style scoped>
.pass-card {
	background: var(--card);
	border: 1px solid var(--border);
	border-radius: var(--radius);
	box-shadow: var(--shadow-sm);
	margin-bottom: var(--spacing-md);
	overflow: hidden;
}
.pass-header {
	display: flex;
	align-items: center;
	padding: var(--spacing-md) var(--spacing-lg);
	background: var(--muted);
	border-bottom: 1px solid var(--border);
	gap: var(--spacing-md);
}
.pass-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: var(--radius-full);
	background: var(--primary);
	color: var(--primary-foreground);
	font-weight: var(--font-weight-semibold);
	font-size: var(--font-size-sm);
	flex-shrink: 0;
}
.pass-title-block {
	flex: 1;
	min-width: 0;
}
.pass-title {
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-semibold);
	margin: 0;
	line-height: var(--line-height-tight);
	color: var(--foreground);
}
.pass-controls {
	display: flex;
	align-items: center;
	gap: var(--spacing-md);
	flex-shrink: 0;
}
.pass-toggle {
	margin: 0;
	padding: 0;
	font-size: var(--font-size-sm);
	color: var(--foreground);
}
.pass-toggle ::v-deep .v-input__control,
.pass-toggle ::v-deep .v-input__slot {
	margin: 0;
}
.pass-toggle ::v-deep .v-label {
	font-size: var(--font-size-sm);
	color: var(--foreground);
}
.rule-header {
	display: grid;
	grid-template-columns: 1fr minmax(220px, 1.6fr) 90px 32px;
	padding: var(--spacing-sm) var(--spacing-lg);
	background: var(--card);
	border-bottom: 1px solid var(--border);
	font-size: var(--font-size-xs);
	text-transform: uppercase;
	color: var(--muted-foreground);
	font-weight: var(--font-weight-semibold);
	letter-spacing: 0.05em;
	gap: var(--spacing-sm);
}
.rule-header--centre {
	text-align: center;
}
.add-rule {
	width: 100%;
	padding: var(--spacing-sm) var(--spacing-lg);
	background: var(--muted);
	color: var(--primary);
	font-size: var(--font-size-sm);
	font-weight: var(--font-weight-medium);
	cursor: pointer;
	border: none;
	border-top: 1px solid var(--border);
	text-align: left;
}
.add-rule:hover {
	background: var(--secondary);
}
.add-rule:focus {
	outline: 2px solid var(--primary);
	outline-offset: -2px;
}
</style>
